import React, { useState, useEffect } from 'react';
import { 
  Heart, 
  Save, 
  Plus, 
  Trash2, 
  Upload, 
  Clock, 
  Eye, 
  X, 
  Edit3,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '../../../components/Toast';
import { getImageUrl as resolveMediaSrc } from '../../../utils/mediaUtils';
import './FormsUnified.css';

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...extra
  };
};

const labelMap = {
  kicker: 'Wellbeing Section Tag / Kicker',
  title: 'Wellbeing Main Heading',
  description: 'Wellbeing Overview Description',
  images: 'Wellbeing Gallery Photos',
  pillars: 'Core SEL Pillars',
  supports: 'Support Pathways'
};

const ICON_OPTIONS = [
  { value: 'fa-user-tie', label: 'Counselor / Advisor (Tie)' },
  { value: 'fa-users', label: 'Group / Advisory (Users)' },
  { value: 'fa-book', label: 'Academic / Study (Book)' },
  { value: 'fa-home', label: 'Family / Home (House)' },
  { value: 'fa-handshake', label: 'Mentoring / Partnership (Handshake)' },
  { value: 'fa-heart', label: 'Empathy / Care (Heart)' },
  { value: 'fa-brain', label: 'Mental / SEL (Brain)' },
  { value: 'fa-star', label: 'Excellence (Star)' },
  { value: 'fa-lightbulb', label: 'Insight / Innovation (Lightbulb)' },
  { value: 'fa-graduation-cap', label: 'Academic (Graduation Cap)' },
  { value: 'fa-trophy', label: 'Achievement (Trophy)' },
  { value: 'fa-shield', label: 'Safety / Protection (Shield)' },
  { value: 'fa-smile', label: 'Positive Wellbeing (Smile)' },
  { value: 'fa-hands-helping', label: 'Support / Helping Hands' },
  { value: 'fa-comments', label: 'Dialogue / Communication' },
  { value: 'fa-user-friends', label: 'Peer Friendship' }
];

const DEFAULT_SUPPORTS = [
  { title: 'Counselor Check-ins', detail: 'Regular sessions for guidance, emotional support, and personal goal planning.', icon: 'fa-user-tie' },
  { title: 'Advisory Circles', detail: 'Weekly small-group conversations that build trust and communication skills.', icon: 'fa-users' },
  { title: 'Academic Coaching', detail: 'Study strategies, time management, and learning support tailored to each student.', icon: 'fa-book' },
  { title: 'Family Partnership', detail: 'Parent updates, workshops, and shared action plans for wellbeing at home.', icon: 'fa-home' },
  { title: 'Peer Mentoring', detail: 'Older students support younger peers through buddy programs, fostering leadership and belonging.', icon: 'fa-handshake' }
];

const DEFAULT_PILLARS = [
  'Self-awareness',
  'Emotional regulation',
  'Empathy & kindness',
  'Goal setting',
  'Positive relationships'
];

export default function StudentLifeWellbeingForm({ apiBase }) {
  const { toast, ToastContainer } = useToast();

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    pillars: [],
    supports: [],
    images: []
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    pillars: false,
    supports: false,
    images: false
  });

  const [originalData, setOriginalData] = useState({ ...formData });
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [mediaMap, setMediaMap] = useState({});
  const [imageUploading, setImageUploading] = useState({});
  const [imageProgress, setImageProgress] = useState({});
  const [lightboxImage, setLightboxImage] = useState(null);

  // Modal state for editing support pathways
  const [editingSupportIndex, setEditingSupportIndex] = useState(null);
  const [modalSupportData, setModalSupportData] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([loadMediaMap(), loadSection()]);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadMediaMap = async () => {
    try {
      const response = await fetch(
        `${apiBase}/studentlife/cms/media?_t=${Date.now()}`,
        { 
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' })
        }
      );
      
      if (!response.ok) return;
      
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach((media) => {
          const mediaId = media.id || media.media_id;
          const mediaUrl = media.url || media.file_path || media.filepath || '';
          if (mediaId && mediaUrl) {
            map[mediaId] = mediaUrl;
            map[String(mediaId)] = mediaUrl;
            const numId = Number(mediaId);
            if (!isNaN(numId)) map[numId] = mediaUrl;
          }
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Media fetch error:', err.message);
    }
  };

  const getMediaUrl = (mediaId) => {
    if (!mediaId) return null;
    const str = String(mediaId).trim();
    if (!str) return null;

    if (str.startsWith('http://') || str.startsWith('https://') || str.startsWith('data:')) {
      return str;
    }

    if (str.startsWith('/')) {
      const origin = (apiBase || '').replace(/\/api\/?$/, '');
      return origin ? `${origin}${str}` : str;
    }

    if (mediaMap[str]) {
      const mapped = mediaMap[str];
      if (mapped.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${mapped}` : mapped;
      }
      return mapped;
    }

    if (mediaMap[mediaId]) {
      const mapped = mediaMap[mediaId];
      if (mapped.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${mapped}` : mapped;
      }
      return mapped;
    }

    const resolved = resolveMediaSrc(mediaId, mediaMap);
    if (resolved) {
      if (resolved.startsWith('/')) {
        const origin = (apiBase || '').replace(/\/api\/?$/, '');
        return origin ? `${origin}${resolved}` : resolved;
      }
      return resolved;
    }

    return str;
  };

  const loadSection = async () => {
    setLoading(true);
    try {
      let wellbeingSection = null;

      const adminResponse = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        {
          credentials: 'include',
          headers: getAuthHeaders({ 'Cache-Control': 'no-cache' })
        }
      );

      if (adminResponse.ok) {
        const result = await adminResponse.json();
        if (result.ok && Array.isArray(result.data)) {
          wellbeingSection = result.data.find(s => s.section_type === 'student-life-wellbeing');
        }
      }

      if (!wellbeingSection) {
        const pubResponse = await fetch(
          `${apiBase}/studentlife/cms/sections?published=true&_t=${Date.now()}`,
          {
            credentials: 'include',
            headers: getAuthHeaders({ 'Cache-Control': 'no-cache' })
          }
        );
        if (pubResponse.ok) {
          const result = await pubResponse.json();
          if (result.ok && Array.isArray(result.data)) {
            wellbeingSection = result.data.find(s => s.section_type === 'student-life-wellbeing');
          }
        }
      }

      if (wellbeingSection) {
        setSectionId(wellbeingSection.id);
        
        let published = wellbeingSection.content_published || wellbeingSection.content || {};
        if (typeof published === 'string') {
          try { published = JSON.parse(published); } catch (e) {}
        }
        
        const enData = published?.en || published || {};
        const newFormData = {
          kicker: enData.kicker || 'Wellbeing & Social Emotional Learning',
          title: enData.title || 'Support for emotional and academic growth',
          description: enData.description || 'Our SEL framework helps students build resilience, confidence, and healthy relationships while supporting academic success. Dedicated counselors, advisory teachers, and structured lessons ensure every student feels seen and supported.',
          pillars: Array.isArray(enData.pillars) && enData.pillars.length > 0 ? enData.pillars : DEFAULT_PILLARS,
          supports: Array.isArray(enData.supports) && enData.supports.length > 0 ? enData.supports : DEFAULT_SUPPORTS,
          images: Array.isArray(enData.images) ? enData.images : []
        };
        
        const pendingMap = {
          kicker: false,
          title: false,
          description: false,
          pillars: false,
          supports: false,
          images: false
        };

        try {
          const pendingRes = await fetch(`${apiBase}/cms/section/${wellbeingSection.id}/with-pending`, {
            credentials: 'include',
            headers: getAuthHeaders()
          });
          if (pendingRes.ok) {
            const pendingData = await pendingRes.json();
            if (pendingData.success && Array.isArray(pendingData.data?.pending_changes)) {
              pendingData.data.pending_changes.forEach(ch => {
                if (ch.status === 'pending' && ch.locale === 'en') {
                  pendingMap[ch.field_id] = true;
                  if (ch.new_value !== null && ch.new_value !== undefined) {
                    let val = ch.new_value;
                    if (typeof val === 'string' && (val.startsWith('{') || val.startsWith('['))) {
                      try { val = JSON.parse(val); } catch (e) {}
                    }
                    newFormData[ch.field_id] = val;
                  }
                }
              });
            }
          }
        } catch (pErr) {
          console.warn('[StudentLifeWellbeingForm] Error loading pending status:', pErr);
        }

        setFormData(newFormData);
        setOriginalData(JSON.parse(JSON.stringify(newFormData)));
        setPendingFields(pendingMap);
      } else {
        toast.info('Wellbeing section not found in database');
      }
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Load error:', err);
      toast.error('Failed to load Wellbeing section');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const hasFieldChanged = (fieldName) => {
    const fieldValue = formData[fieldName];
    const oldValue = originalData[fieldName];
    return JSON.stringify(fieldValue) !== JSON.stringify(oldValue);
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section ID not loaded');
      return;
    }

    const fieldValue = formData[fieldName];
    const oldValue = originalData[fieldName];

    if (!hasFieldChanged(fieldName)) {
      toast.info('No changes detected for ' + (labelMap[fieldName] || fieldName));
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(
        `${apiBase}/cms/field-change/submit`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          credentials: 'include',
          body: JSON.stringify({
            section_id: sectionId,
            field_id: fieldName,
            field_name: labelMap[fieldName] || fieldName,
            old_value: oldValue,
            new_value: fieldValue,
            locale: 'en'
          })
        }
      );

      const result = await response.json();

      if (result.success || response.ok) {
        toast.success(`${labelMap[fieldName] || fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
        setOriginalData(prev => ({ ...prev, [fieldName]: JSON.parse(JSON.stringify(fieldValue)) }));
      } else {
        if (response.status === 409) {
          toast.error('This field already has a pending approval');
        } else {
          toast.error(result.error || result.message || 'Submission failed');
        }
      }
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Submit error:', err);
      toast.error('Submission error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleImageUpload = (imgIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ALLOWED_TYPES.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, or WebP)');
      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Image must be less than 5MB');
      return;
    }

    const key = `img-${imgIndex}`;
    setImageUploading(prev => ({ ...prev, [key]: true }));
    setImageProgress(prev => ({ ...prev, [key]: 0 }));

    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('section_type', 'student-life-wellbeing');

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (ev) => {
        if (ev.lengthComputable) {
          const percentComplete = Math.round((ev.loaded / ev.total) * 100);
          setImageProgress(prev => ({ ...prev, [key]: percentComplete }));
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status === 200 || xhr.status === 201) {
          try {
            const response = JSON.parse(xhr.responseText);

            if (response.success || response.ok) {
              const origin = (apiBase || '').replace(/\/api\/?$/, '');
              const returnedUrl = response.url || response.file_path || (response.filename ? `/api/uploads/studentlife/${response.filename}` : '');
              const fullUrl = returnedUrl.startsWith('http') ? returnedUrl : (returnedUrl ? `${origin}${returnedUrl}` : '');
              const finalUrl = fullUrl || returnedUrl || String(response.media_id || response.id || '');

              if (finalUrl) {
                const newImages = [...formData.images];
                newImages[imgIndex] = finalUrl;
                handleChange('images', newImages);

                const mediaId = response.media_id || response.id;
                if (mediaId) {
                  setMediaMap(prev => ({
                    ...prev,
                    [mediaId]: finalUrl,
                    [String(mediaId)]: finalUrl
                  }));
                }

                toast.success('Image uploaded successfully');
              } else {
                toast.error('No valid image URL returned by server');
              }
            } else {
              toast.error(response.error || response.message || 'Upload failed');
            }
          } catch (parseErr) {
            console.error('[StudentLifeWellbeingForm] JSON parse error:', parseErr);
            toast.error('Invalid server response');
          }
        } else {
          toast.error('Upload failed (HTTP ' + xhr.status + ')');
        }
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.addEventListener('error', () => {
        toast.error('Network error during upload');
        setImageUploading(prev => ({ ...prev, [key]: false }));
      });

      xhr.open('POST', `${apiBase}/studentlife/cms/media/upload`);
      xhr.withCredentials = true;
      const token = localStorage.getItem('ph_auth_token') || localStorage.getItem('auth_token') || sessionStorage.getItem('token');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      xhr.send(formDataUpload);
    } catch (err) {
      console.error('[StudentLifeWellbeingForm] Upload setup error:', err);
      toast.error('Upload setup error: ' + err.message);
      setImageUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  const handleRemoveImage = (imgIndex) => {
    const newImages = [...formData.images];
    newImages[imgIndex] = '';
    handleChange('images', newImages);
  };

  const openSupportModal = (index = null) => {
    if (index !== null) {
      setEditingSupportIndex(index);
      setModalSupportData({ ...formData.supports[index] });
    } else {
      setEditingSupportIndex(null);
      setModalSupportData({
        title: '',
        detail: '',
        icon: 'fa-star'
      });
    }
  };

  const closeSupportModal = () => {
    setEditingSupportIndex(null);
    setModalSupportData(null);
  };

  const handleSaveSupportModal = () => {
    if (!modalSupportData.title?.trim()) {
      toast.error('Pathway title is required');
      return;
    }

    const updated = [...formData.supports];
    if (editingSupportIndex !== null) {
      updated[editingSupportIndex] = modalSupportData;
    } else {
      updated.push(modalSupportData);
    }

    handleChange('supports', updated);
    closeSupportModal();
    toast.success('Support pathway saved to draft');
  };

  const handleDeleteSupport = (index) => {
    const updated = formData.supports.filter((_, i) => i !== index);
    handleChange('supports', updated);
    toast.info('Support pathway removed from draft');
  };

  const handleAddPillar = () => {
    handleChange('pillars', [...formData.pillars, '']);
  };

  const handlePillarChange = (index, value) => {
    const updated = [...formData.pillars];
    updated[index] = value;
    handleChange('pillars', updated);
  };

  const handleDeletePillar = (index) => {
    const updated = formData.pillars.filter((_, i) => i !== index);
    handleChange('pillars', updated);
  };

  if (loading) {
    return (
      <div className="unified-loading-container">
        <div className="unified-loading-spinner" />
        <p className="unified-loading-text">Loading Wellbeing & SEL section...</p>
      </div>
    );
  }

  return (
    <div className="unified-form-container">
      <ToastContainer />

      {/* Header */}
      <div className="unified-header">
        <div className="unified-header-icon">
          <Heart size={22} />
        </div>
        <div>
          <h2 className="unified-title">Wellbeing & Social Emotional Learning</h2>
          <p className="unified-subtitle">
            Manage wellbeing framework, core pillars, support pathways, and 1200 × 500 px visual showcase
          </p>
        </div>
      </div>

      {/* Field 1: Kicker */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Section Tag / Kicker</label>
          {pendingFields.kicker && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.kicker}
            onChange={(e) => handleChange('kicker', e.target.value)}
            disabled={submitting.kicker || pendingFields.kicker}
            placeholder="e.g. Wellbeing & Social Emotional Learning"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('kicker')}
            disabled={submitting.kicker || pendingFields.kicker || !hasFieldChanged('kicker')}
          >
            <Save size={14} /> {submitting.kicker ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 2: Title */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Main Heading</label>
          {pendingFields.title && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row">
          <input
            type="text"
            className="unified-input"
            value={formData.title}
            onChange={(e) => handleChange('title', e.target.value)}
            disabled={submitting.title || pendingFields.title}
            placeholder="e.g. Support for emotional and academic growth"
          />
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('title')}
            disabled={submitting.title || pendingFields.title || !hasFieldChanged('title')}
          >
            <Save size={14} /> {submitting.title ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 3: Description */}
      <div className="unified-field-card">
        <div className="unified-field-header">
          <label className="unified-label">Overview Description</label>
          {pendingFields.description && (
            <span className="unified-pending-pill"><Clock size={12} /> Pending Review</span>
          )}
        </div>
        <div className="unified-input-action-row" style={{ alignItems: 'flex-start' }}>
          <textarea
            className="unified-textarea"
            rows={3}
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            disabled={submitting.description || pendingFields.description}
            placeholder="Explain the SEL framework and how students are nurtured..."
          />
          <button
            type="button"
            className="unified-submit-button"
            style={{ marginTop: '2px' }}
            onClick={() => handleSubmitField('description')}
            disabled={submitting.description || pendingFields.description || !hasFieldChanged('description')}
          >
            <Save size={14} /> {submitting.description ? 'Saving...' : 'Save & Submit'}
          </button>
        </div>
      </div>

      {/* Field 4: Wellbeing Showcase Carousel (3 Slides) */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Wellbeing Gallery Carousel (3 Slides)</label>
            <p className="unified-field-desc">
              Three banner images rotating in the public Wellbeing section (recommended: 1200 × 500 px)
            </p>
          </div>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('images')}
            disabled={submitting.images || pendingFields.images || !hasFieldChanged('images')}
          >
            <Save size={14} />
            {submitting.images ? 'Saving...' : 'Save & Submit All Photos'}
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginTop: '14px' }}>
          {[0, 1, 2].map((imgIndex) => {
            const imgId = formData.images?.[imgIndex];
            const imgUrl = getMediaUrl(imgId);
            const uploadKey = `img-${imgIndex}`;
            const isUploading = imageUploading[uploadKey];
            const progress = imageProgress[uploadKey] || 0;

            return (
              <div
                key={imgIndex}
                style={{
                  background: '#f8fafc',
                  borderRadius: '12px',
                  border: '1px solid #e2e8f0',
                  overflow: 'hidden',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div
                  style={{
                    height: '160px',
                    background: '#0f172a',
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {imgUrl ? (
                    <>
                      <img
                        src={imgUrl}
                        alt={`Slide ${imgIndex + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          right: '8px',
                          display: 'flex',
                          gap: '6px'
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setLightboxImage(imgUrl)}
                          style={{
                            background: 'rgba(0,0,0,0.65)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Preview full photo"
                        >
                          <Eye size={12} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(imgIndex)}
                          style={{
                            background: 'rgba(239, 68, 68, 0.85)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            padding: '4px 6px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center'
                          }}
                          title="Remove image"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                    </>
                  ) : (
                    <div style={{ color: '#94a3b8', textAlign: 'center' }}>
                      <ImageIcon size={32} style={{ margin: '0 auto 6px', opacity: 0.6 }} />
                      <p style={{ margin: 0, fontSize: '13px' }}>Slide #{imgIndex + 1} Empty</p>
                      <p style={{ margin: '2px 0 0', fontSize: '11px', opacity: 0.7 }}>1200 × 500 px</p>
                    </div>
                  )}
                </div>

                <div
                  style={{
                    padding: '12px 14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    background: '#ffffff',
                    borderTop: '1px solid #e2e8f0'
                  }}
                >
                  <div>
                    <span style={{ fontSize: '13px', fontWeight: '800', color: '#0f172a' }}>
                      Slide #{imgIndex + 1}
                    </span>
                    {imgId && (
                      <span style={{ display: 'block', fontSize: '11px', color: '#64748b' }}>
                        {String(imgId).startsWith('http') ? 'Direct URL' : `ID: ${String(imgId).slice(0, 14)}...`}
                      </span>
                    )}
                  </div>

                  <label className="unified-secondary-btn" style={{ cursor: isUploading ? 'not-allowed' : 'pointer', padding: '6px 12px', fontSize: '12.5px', display: 'inline-flex' }}>
                    <Upload size={13} />
                    <span>
                      {isUploading
                        ? `Uploading ${progress}%`
                        : imgUrl ? 'Replace' : 'Upload'}
                    </span>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => handleImageUpload(imgIndex, e)}
                      disabled={isUploading}
                      style={{ display: 'none' }}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Field 5: Core Pillars */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Core Pillars ({formData.pillars.length})</label>
            <p className="unified-field-desc">
              Key personal competencies and values developed through the wellbeing program
            </p>
          </div>
          <button
            type="button"
            className="unified-submit-button"
            onClick={() => handleSubmitField('pillars')}
            disabled={submitting.pillars || pendingFields.pillars || !hasFieldChanged('pillars')}
          >
            <Save size={14} />
            {submitting.pillars ? 'Saving...' : 'Save & Submit Pillars'}
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '10px' }}>
          {formData.pillars.map((pillar, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}
            >
              <span
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#eff6ff',
                  color: '#2563eb',
                  fontWeight: '800',
                  fontSize: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                {idx + 1}
              </span>
              <input
                type="text"
                className="unified-input"
                value={pillar}
                onChange={(e) => handlePillarChange(idx, e.target.value)}
                placeholder={`e.g. Pillar ${idx + 1}`}
                disabled={submitting.pillars || pendingFields.pillars}
              />
              <button
                type="button"
                className="unified-secondary-btn"
                style={{ color: '#ef4444', borderColor: '#fca5a5', padding: '8px' }}
                onClick={() => handleDeletePillar(idx)}
                disabled={submitting.pillars || pendingFields.pillars}
                title="Delete pillar"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}

          <button
            type="button"
            className="unified-secondary-btn"
            style={{ alignSelf: 'flex-start', marginTop: '4px' }}
            onClick={handleAddPillar}
            disabled={submitting.pillars || pendingFields.pillars}
          >
            <Plus size={14} /> Add Core Pillar
          </button>
        </div>
      </div>

      {/* Field 6: Support Pathways */}
      <div className="unified-field-card">
        <div className="unified-card-header">
          <div>
            <label className="unified-field-label">Support Pathways ({formData.supports.length})</label>
            <p className="unified-field-desc">
              Structured advisory channels, counselor sessions, coaching, and family partnership
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              className="unified-secondary-btn"
              onClick={() => openSupportModal(null)}
              disabled={submitting.supports || pendingFields.supports}
            >
              <Plus size={14} /> Add Pathway
            </button>
            <button
              type="button"
              className="unified-submit-button"
              onClick={() => handleSubmitField('supports')}
              disabled={submitting.supports || pendingFields.supports || !hasFieldChanged('supports')}
            >
              <Save size={14} />
              {submitting.supports ? 'Saving...' : 'Save & Submit Pathways'}
            </button>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginTop: '10px' }}>
          {formData.supports.map((sup, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                padding: '14px 18px',
                background: '#f8fafc',
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                transition: 'all 0.2s ease'
              }}
            >
              {/* Icon Preview */}
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '10px',
                  background: '#eff6ff',
                  color: '#2563eb',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '20px',
                  flexShrink: 0
                }}
              >
                <i className={`fas ${sup.icon || 'fa-star'}`} />
              </div>

              {/* Details */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <h4 style={{ margin: '0 0 3px', fontSize: '15px', fontWeight: '800', color: '#0f172a' }}>
                  {sup.title || 'Untitled Pathway'}
                </h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#475569', lineHeight: 1.4 }}>
                  {sup.detail || 'No description provided.'}
                </p>
              </div>

              {/* Actions */}
              <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                <button
                  type="button"
                  className="unified-secondary-btn"
                  onClick={() => openSupportModal(idx)}
                  disabled={submitting.supports || pendingFields.supports}
                >
                  <Edit3 size={13} /> Edit
                </button>
                <button
                  type="button"
                  className="unified-secondary-btn"
                  style={{ color: '#ef4444', borderColor: '#fca5a5' }}
                  onClick={() => handleDeleteSupport(idx)}
                  disabled={submitting.supports || pendingFields.supports}
                >
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Support Pathway Modal */}
      {modalSupportData && (
        <div className="unified-modal-overlay" onClick={closeSupportModal}>
          <div className="unified-modal" onClick={(e) => e.stopPropagation()}>
            <div className="unified-modal-header">
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>
                {editingSupportIndex !== null ? 'Edit Support Pathway' : 'New Support Pathway'}
              </h3>
              <button
                type="button"
                onClick={closeSupportModal}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}
              >
                <X size={20} />
              </button>
            </div>

            <div className="unified-modal-body">
              <div style={{ display: 'grid', gap: '16px' }}>
                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Pathway Title *</label>
                  <input
                    type="text"
                    className="unified-input"
                    value={modalSupportData.title}
                    onChange={(e) => setModalSupportData({ ...modalSupportData, title: e.target.value })}
                    placeholder="e.g. Counselor Check-ins"
                  />
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '6px' }}>Description / Detail</label>
                  <textarea
                    className="unified-textarea"
                    rows={3}
                    value={modalSupportData.detail}
                    onChange={(e) => setModalSupportData({ ...modalSupportData, detail: e.target.value })}
                    placeholder="Explain how this pathway supports the students..."
                  />
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '4px', display: 'block' }}>
                    Press Enter to add multiple lines if needed.
                  </span>
                </div>

                <div>
                  <label className="unified-label" style={{ display: 'block', marginBottom: '8px' }}>Select Icon</label>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '8px' }}>
                    {ICON_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setModalSupportData({ ...modalSupportData, icon: opt.value })}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 10px',
                          borderRadius: '8px',
                          border: modalSupportData.icon === opt.value ? '2px solid #2563eb' : '1px solid #e2e8f0',
                          background: modalSupportData.icon === opt.value ? '#eff6ff' : '#ffffff',
                          cursor: 'pointer',
                          fontSize: '12px',
                          color: modalSupportData.icon === opt.value ? '#2563eb' : '#334155',
                          fontWeight: modalSupportData.icon === opt.value ? '700' : '500',
                          textAlign: 'left'
                        }}
                      >
                        <i className={`fas ${opt.value}`} style={{ fontSize: '14px', width: '16px' }} />
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {opt.label.split(' ')[0]}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="unified-modal-footer">
              <button type="button" className="unified-secondary-btn" onClick={closeSupportModal}>
                Cancel
              </button>
              <button type="button" className="unified-submit-button" onClick={handleSaveSupportModal}>
                <Save size={14} />
                <span>Save to Draft</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div className="unified-modal-overlay" onClick={() => setLightboxImage(null)}>
          <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }}>
            <button
              type="button"
              onClick={() => setLightboxImage(null)}
              style={{
                position: 'absolute',
                top: '-12px',
                right: '-12px',
                background: '#ef4444',
                color: '#fff',
                border: 'none',
                borderRadius: '50%',
                width: '28px',
                height: '28px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                zIndex: 10
              }}
            >
              <X size={16} />
            </button>
            <img
              src={lightboxImage}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: '85vh', borderRadius: '12px', display: 'block' }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
